import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  LinearProgress,
  Chip,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Icons
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

interface PracticeContent {
  id: number;
  type: 'listening' | 'typing' | 'matching';
  instruction: string;
  content: string;
  answer: string;
  audioUrl?: string;
  hints?: string[];
}

interface PracticeSessionProps {
  content: PracticeContent[];
  onComplete: (results: any) => void;
  onProgressUpdate: (progress: number) => void;
}

const PracticeCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

const PracticeSession: React.FC<PracticeSessionProps> = ({ 
  content, 
  onComplete, 
  onProgressUpdate 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<{ [key: number]: string }>({});
  const [feedback, setFeedback] = useState<{ [key: number]: 'correct' | 'incorrect' | null }>({});
  const [sessionComplete, setSessionComplete] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // Mock practice content if none provided
  const exercises = content.length > 0 ? content : [
    {
      id: 1,
      type: 'listening' as const,
      instruction: 'Listen to the audio and type what you hear:',
      content: 'The weather is beautiful today.',
      answer: 'The weather is beautiful today.',
      hints: ['Listen carefully to each word', 'Pay attention to pronunciation'],
    },
    {
      id: 2,
      type: 'typing' as const,
      instruction: 'Complete this sentence with the correct word:',
      content: 'She is very _____ about her studies.',
      answer: 'serious',
      hints: ['Think about someone who works hard', 'Starts with "s"'],
    },
    {
      id: 3,
      type: 'listening' as const,
      instruction: 'Listen and write the complete sentence:',
      content: 'I am learning English every day.',
      answer: 'I am learning English every day.',
      hints: ['Focus on the verb form', 'Think about daily activities'],
    },
  ];

  const currentExercise = exercises[currentIndex];
  const totalExercises = exercises.length;
  const progress = ((currentIndex + 1) / totalExercises) * 100;

  useEffect(() => {
    onProgressUpdate(progress);
  }, [progress, onProgressUpdate]);

  const handleAnswerChange = (value: string) => {
    setUserAnswers(prev => ({
      ...prev,
      [currentExercise.id]: value
    }));
    
    // Clear previous feedback when answer changes
    setFeedback(prev => ({
      ...prev,
      [currentExercise.id]: null
    }));
  };

  const checkAnswer = () => {
    const userAnswer = userAnswers[currentExercise.id]?.trim().toLowerCase() || '';
    const correctAnswer = currentExercise.answer.trim().toLowerCase();
    
    const isCorrect = userAnswer === correctAnswer;
    
    setFeedback(prev => ({
      ...prev,
      [currentExercise.id]: isCorrect ? 'correct' : 'incorrect'
    }));
  };

  const playAudio = () => {
    // Mock audio playback - in real implementation, you'd play the audio file
    const speech = new SpeechSynthesisUtterance(currentExercise.content);
    speech.rate = 0.8;
    window.speechSynthesis.speak(speech);
  };

  const handleNext = () => {
    if (currentIndex < totalExercises - 1) {
      setCurrentIndex(prev => prev + 1);
      setShowHint(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setShowHint(false);
    }
  };

  const handleComplete = () => {
    const correctAnswers = Object.keys(feedback).filter(
      id => feedback[parseInt(id)] === 'correct'
    ).length;
    
    const results = {
      totalExercises: totalExercises,
      completedExercises: Object.keys(userAnswers).length,
      correctAnswers: correctAnswers,
      accuracy: Math.round((correctAnswers / totalExercises) * 100),
      completedAt: new Date(),
    };

    setSessionComplete(true);
    onProgressUpdate(100);
    onComplete(results);
  };

  const toggleHint = () => {
    setShowHint(!showHint);
  };

  if (sessionComplete) {
    const completedExercises = Object.keys(userAnswers).length;
    const correctAnswers = Object.keys(feedback).filter(id => feedback[parseInt(id)] === 'correct').length;
    const accuracy = Math.round((correctAnswers / totalExercises) * 100);

    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 2, color: 'primary.main' }}>
            Practice Session Complete! 🎯
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="div" sx={{ mb: 1, fontWeight: 'bold', color: 'success.main' }}>
              {accuracy}%
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You completed {completedExercises} exercises with {correctAnswers} correct answers
            </Typography>
          </Box>

          <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                {correctAnswers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Correct
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                {totalExercises - correctAnswers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Incorrect
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            size="large"
            onClick={() => onComplete({ accuracy, correctAnswers, totalExercises })}
            sx={{ mt: 3, px: 4 }}
          >
            Continue to My Classroom
          </Button>
        </CardContent>
      </Card>
    );
  }

  const currentAnswer = userAnswers[currentExercise.id];
  const currentFeedback = feedback[currentExercise.id];
  const isLastExercise = currentIndex === totalExercises - 1;

  return (
    <Box>
      {/* Progress Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">
            Exercise {currentIndex + 1} of {totalExercises}
          </Typography>
          <Chip 
            label={`${Math.round(progress)}% Complete`} 
            color="primary" 
            variant="outlined" 
          />
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'primary.main',
              borderRadius: 4,
            },
          }}
        />
      </Box>

      {/* Exercise */}
      <PracticeCard>
        <CardContent sx={{ py: 4 }}>
          <Typography variant="h6" component="h3" sx={{ mb: 3, fontWeight: 'medium' }}>
            {currentExercise.instruction}
          </Typography>

          {/* Content Display */}
          {currentExercise.type === 'listening' && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<VolumeUpIcon />}
                onClick={playAudio}
                size="large"
                sx={{ mb: 2 }}
              >
                Play Audio
              </Button>
              <Typography variant="body2" color="text.secondary">
                Click to listen to the audio
              </Typography>
            </Box>
          )}

          {currentExercise.type === 'typing' && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ textAlign: 'center' }}>
                {currentExercise.content}
              </Typography>
            </Box>
          )}

          {/* Answer Input */}
          <TextField
            fullWidth
            label="Your answer"
            value={currentAnswer || ''}
            onChange={(e) => handleAnswerChange(e.target.value)}
            multiline={currentExercise.type === 'listening'}
            rows={currentExercise.type === 'listening' ? 2 : 1}
            sx={{ mb: 2 }}
          />

          {/* Feedback */}
          {currentFeedback && (
            <Box sx={{ mb: 2 }}>
              {currentFeedback === 'correct' ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  Correct! Well done.
                </Alert>
              ) : (
                <Alert severity="error" icon={<ErrorIcon />}>
                  Not quite right. The correct answer is: "{currentExercise.answer}"
                </Alert>
              )}
            </Box>
          )}

          {/* Hint Section */}
          {currentExercise.hints && !currentFeedback && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="text"
                onClick={toggleHint}
                size="small"
                sx={{ mb: 1 }}
              >
                {showHint ? 'Hide Hint' : 'Show Hint'}
              </Button>
              {showHint && (
                <Alert severity="info">
                  <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                    {currentExercise.hints.map((hint, index) => (
                      <li key={index}>
                        <Typography variant="body2">{hint}</Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
            </Box>
          )}

          {/* Check Answer Button */}
          {!currentFeedback && (
            <Button
              variant="contained"
              onClick={checkAnswer}
              disabled={!currentAnswer?.trim()}
              sx={{ mr: 2 }}
            >
              Check Answer
            </Button>
          )}
        </CardContent>
      </PracticeCard>

      {/* Navigation */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          onClick={handlePrevious}
          disabled={currentIndex === 0}
        >
          Previous
        </Button>

        <Typography variant="body2" color="text.secondary">
          {Object.keys(userAnswers).length} of {totalExercises} completed
        </Typography>

        {isLastExercise ? (
          <Button
            variant="contained"
            endIcon={<CheckCircleIcon />}
            onClick={handleComplete}
            color="success"
          >
            Finish Session
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<NavigateNextIcon />}
            onClick={handleNext}
          >
            Next Exercise
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default PracticeSession;